<script setup lang="ts">
  import { RouterView, useRoute } from 'vue-router';
  import { defineAsyncComponent, ref, watch, onMounted, onBeforeUnmount } from 'vue';
  import { useMenuStore } from "@/store/index";
  import { storeToRefs } from 'pinia';
	import { ResponseMenu, Product, CardClass } from './models/responseMenu.interface';
	import menuService from '@/services/menu';
	import TrackService from '@/services/track-custom';
	import { useClickEvent } from './helpers/composables';
	import { useI18n } from 'vue-i18n';
  import supplierSurveys from './services/supplier-surveys';
  import { ResponseTypeform } from './models/responseTypeForm.interface';
  import { useDevice } from './helpers/device-composables';

  const { detectOsType, detectScreenSize, detectOrientation } = useDevice();
  const { findProduct } = useClickEvent();
  const menuStore = useMenuStore();
  const {
    menuData,productClicked,skeletonLoad,
    trakingData,customerData,currentSupplier,
    customerSuppliers,contentPopups,defaultLanguageLocalStorage,
    profileData,typeFormData,flatSplashScreenCluvi,
    flagCssCustomObj, allergenPopup, TypeOS, TypeDevice, TypeOrientation,
    isMenuPdfView, classGridMenu
  } = storeToRefs(menuStore);
  const { trakingNewMenu, setMenu } = menuStore;
  const route = useRoute();
  const { locale } = useI18n({ useScope: "global" });

  const Header = defineAsyncComponent(() => import("@/components/header/Header.vue"));
  const FooterLink = defineAsyncComponent(() => import("@/components/footerLink/FooterLink.vue"));
  const Modal = defineAsyncComponent(() => import("@/components/modal/Modal.vue"));
  const ProductDetail = defineAsyncComponent(() => import("@/components/productDetail/ProductDetail.vue"));
  const ModalRatingsStar = defineAsyncComponent(()=> import("@/components/modal/ModalRatingsStar.vue"));
  const ModalLang = defineAsyncComponent(()=>import("@/components/modal/ModalLang.vue"));
  const MenuOpciones = defineAsyncComponent(() => import("@/components/menuOpciones/MenuOpciones.vue"));
  const buttonRatings = defineAsyncComponent(()=>import("@/components/button/ButtonRatings.vue"));
  const buttonLeftRatings = defineAsyncComponent(()=>import("@/components/button/ButtonLeftRatings.vue"));
  const ButtonViewMenu = defineAsyncComponent(()=>import("@/components/button/ButtonViewMenu.vue"));
  const CustomButtonFloat = defineAsyncComponent(()=>import("@/components/button/CustomButtonFloat.vue"));
  const OrganismSplashCluvi = defineAsyncComponent(() => import("@/components/organisms/OrganismSplashCluvi.vue"));

  const displayProduct = ref(false);
  let langModal = ref<Boolean>(false);
  let menuOptionsModal = ref<Boolean>(false);
  let flatLanguageChange = ref<Boolean>(false);
  let openModalRatingStar = ref<Boolean>(false);
  let floatButtonRate = ref<Boolean>(false);
  let offRate = ref<boolean>(false);
  let letFlatTypeForm = ref<boolean>(false);
  let LanguageCluviOnAdds = ref<boolean>(false);
  let FlagsAreLoading = ref<Boolean>(true);
  let showCustomButtonFloat = ref<boolean>(false);
  const renderKey = ref(0);

  watch(defaultLanguageLocalStorage, (valNew,valOld) => {
    if (valOld && (valNew !== valOld)) saveLanguageModalButton(valNew);
  });
  watch(
    () => route.query?.product,
    (newId) => {
      if(newId) {
        var product = findProduct(newId as string);
        productClicked.value = product;
      } else {
        productClicked.value = {} as Product;
        displayProduct.value = false;
      }
    }
  );
  watch(productClicked, (newProductClicked) => {
    displayProduct.value = !!Object.keys(newProductClicked)?.length;
  });
  watch(openModalRatingStar, (val) => {
    if(val) NoScrollBody();
    else scrollBody();
  });

  const checkParamsQueryRoute = () => {
    const supplier_id: number = route.params.supplier_id ?  Number(route.params.supplier_id) : 0;
    if(supplier_id === 29307) showCustomButtonFloat.value = true;
    const service: string = route.params.service ? route.params.service as string : '';
    const subservice: string = route.params.subservice ? route.params.subservice as string : '';
    const product_id: string = route.query.product ? route.query.product as string : '';
    const lang: any = (route.name === 'MenuPdfView' && route.query?.language !== undefined)
      ? (Array.isArray(route.query.language)
        ? route.query.language[0]
        : typeof route.query.language === 'string'
          ? route.query.language
          : String(route.query.language))
      : defaultLanguageLocalStorage.value.toLocaleLowerCase();

    trakingData.value.supplier_id = supplier_id;
    trakingData.value.service = service;
    trakingData.value.subservice = subservice;

    getCssPropertiesFlags();
    getMenu(supplier_id, service, subservice, product_id, lang);
    getActiveTypeForm();
  };
  const menuRequest = async (supplier_id: number, service: string, subservice: string, lang: string, cached: boolean) => {
    if (cached) return menuService.getMenuCache(supplier_id,service,subservice,'en-menu',lang);
    return menuService.getMenuNonCache(supplier_id,service,subservice,'en-menu',lang);
  };
  const menuResponse = async (supplier_id: number, service: string, subservice: string, lang: string) => {
    const menuData: ResponseMenu = (await menuRequest(supplier_id,service,subservice,lang,true)).data;
    if (!menuData.customer.menu_cached) return (await menuRequest(supplier_id, service, subservice, lang, false)).data;
    return menuData;
  }
  const getMenu = async (supplier_id: number, service: string, subservice: string, product_id: string, lang: string, cached: boolean = true) =>  {
    try {
			skeletonLoad.value = true;
      const menuData: ResponseMenu = await menuResponse(supplier_id, service, subservice, lang);
      if (menuData) {
        setInfoMenuAndCurrenci(menuData);
        trakingNewMenu();
      };
      if (product_id) {
        const product = findProduct(product_id);
        document.title = product.label;
        productClicked.value = product;
      };
      flatSplashScreenCluvi.value = false;
      renderKey.value++;
    } catch (error) {
			skeletonLoad.value = false;
      console.error(error);
      const menuData:ResponseMenu = (await menuService.getMenuNonCache(supplier_id,service,subservice, 'en-menu',lang)).data;
      if (menuData) setInfoMenuAndCurrenci(menuData);
    };
  };
  const setInfoMenuAndCurrenci = (menuData:ResponseMenu) => {
    TrackService.TrackEventCluvi({},'load new menu');
    setMenu(menuData);
    customerData.value = menuData.customer;
    customerData.value.pixel_google_analytics = 'G-VNP5KSNBM7';
    currentSupplier.value = menuData.current_supplier;
    customerSuppliers.value = menuData.customer_suppliers;
    contentPopups.value = menuData.content_popups;
		skeletonLoad.value = false;
    if(!flatLanguageChange.value) setLanguageMenu();
    showButtonFloat();
  };
  const setLanguageMenu = () => {
    LanguageCluviOnAdds.value = menuData.value.cluvi_add_ons.includes('multilanguage');
    if (customerData.value.langs.length === 0) setI18nFileByLang(customerData.value.language);
    if (customerData.value.langs.length) {
      let dataLanguagesArray:string[] = [];
      dataLanguagesArray = dataLanguagesArray.concat(customerData.value.langs);
      dataLanguagesArray = dataLanguagesArray.concat(customerData.value.language);
      const dataArr = new Set(dataLanguagesArray);
      const languagesArr = [...dataArr];
      var languageBrowser = navigator.language.substr(0,2).toLocaleUpperCase();
      let languageFind = languagesArr.find(item => item === languageBrowser);
      if (!defaultLanguageLocalStorage.value && languageFind) {
        defaultLanguageLocalStorage.value = languageFind;
        setI18nFileByLang(defaultLanguageLocalStorage.value);
      } else if (defaultLanguageLocalStorage.value && languageFind) {
        setI18nFileByLang(defaultLanguageLocalStorage.value);
      } else if (defaultLanguageLocalStorage.value && !languageFind) {
        setI18nFileByLang(defaultLanguageLocalStorage.value);
        if (LanguageCluviOnAdds.value) langModal.value = true;
      } else {
        defaultLanguageLocalStorage.value = customerData.value.language;
        setI18nFileByLang(defaultLanguageLocalStorage.value);
        if (LanguageCluviOnAdds.value) langModal.value = true;
      };
    };
  };
	const setI18nFileByLang = (language: string) => {
    locale.value = language.toLocaleLowerCase();
    defaultLanguageLocalStorage.value = language;
  };
  const closeModalLang = () => {
    langModal.value = false;
  };
  const saveLanguageModalButton = (lang: string) => {
    defaultLanguageLocalStorage.value = lang;
    flatLanguageChange.value = true;
    checkParamsQueryRoute();
    setTimeout(() => closeModalLang(), 2000);
  };
  const openModalRate = () => {
    openModalRatingStar.value = true;
  };
  const closeModalRatingsStar = () => {
    openModalRatingStar.value = false;
  };
  const NoScrollBody = () => {
    document.body.classList.add('no-scroll');
  };
  const scrollBody = () => {
    document.body.classList.remove('no-scroll');
  };
  const hiddenButtonFloat = () => {
    floatButtonRate.value = false;
  };
  const showButtonFloat = () => {
    if (customerData.value.rate_nps === false && customerData.value.rate_digitalmenu === false) {
      floatButtonRate.value = false;
    } else {
      TrackService.TrackEventCluvi({},'has rate');
      setTimeout(() => {
        floatButtonRate.value = flagCssCustomObj.value.flagFloatingButtonSurvey ? true : false;
      }, 5000);
    }
  };
  const getTypesForm = async (supplier_id:number) => {
    try {
      const responseTypeForm:ResponseTypeform = (await supplierSurveys.getSupplierSurveys(supplier_id)).data;
      if (responseTypeForm) {
        letFlatTypeForm.value = true;
        typeFormData.value = responseTypeForm as any;
      };
    } catch (error) {
      console.error(error);
    };
  };
  const getActiveTypeForm = async() => {
    const supplier_id: number = route.params.supplier_id ?  Number(route.params.supplier_id) : 0;
    try {
      const responseActivesForms = (await supplierSurveys.getSupplierSurveysHideRate(supplier_id)).data;
      if (responseActivesForms && !letFlatTypeForm.value) getTypesForm(supplier_id);
    } catch (error) {
      offRate.value = true;
      floatButtonRate.value = false;
      console.error(error);
    };
  };
  const getCssPropertiesFlags = () => {
    const supplierId = currentSupplier.value.id ? currentSupplier.value.id : Number(route.params.supplier_id);
    menuService.getCssProperties(supplierId)
      .then(response => {
        const archies_suppliers = [10666,10667,22282,1834,1836,1844,1855,3093,12363,1856,1959,1831,4065,2517,2516,2519,2521,2520,1845,2518,1847,1832,1837,5560,1833,1850,1835,1853,1839,1830,1843,1848,1846,1849,1838,1851,1854,1842,1840,1961,1841,1852]
        let responseFlagCssCustom = response.data;
        if(archies_suppliers.includes(supplierId)){
          responseFlagCssCustom.flagAlertGridProducts = false;
          responseFlagCssCustom.flagAllergenPopup = false;
          responseFlagCssCustom.flagSliderCategoriesImage = false;
          responseFlagCssCustom.flagViewGridProducts = false;
          responseFlagCssCustom.flagViewButtonRightCategories = true;
        }
        Object.assign(flagCssCustomObj.value, responseFlagCssCustom);
        // Hardcode para supplier 30498
        if(supplierId === 30498) {
          classGridMenu.value = CardClass.IMAGEXL;
        }
        if (!flagCssCustomObj.value.flagAllergenPopup) allergenPopup.value.show = false;
        if (!flagCssCustomObj.value.flagAlertGridProducts){
          let elementFixedwrapShifts = document.querySelector('.wrapShift-fixed');
          if (elementFixedwrapShifts) (elementFixedwrapShifts as HTMLElement).style.top = '70px';
        }
        FlagsAreLoading.value = false;
      })
      .catch(error => {
        FlagsAreLoading.value = false;
        (error);
      })
  };

  const executionOnLocal = () => {
    if(import.meta.env.VITE_ENVIRONMENT_STAGE === 'local'){
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `${import.meta.env.VITE_APP_API_NONCACHE}/api/csscustom/${route.params.supplier_id}.css`;
      document.head.appendChild(link);
    }
  };
  const InputDeviceInfo = () => {
    TypeOS.value = detectOsType();
    TypeDevice.value = detectScreenSize();
    TypeOrientation.value = detectOrientation();
  };

  flatSplashScreenCluvi.value = true;

  onMounted(() => {
    checkParamsQueryRoute();
		trakingNewMenu();
    setTimeout(() => flatSplashScreenCluvi.value = false, 5000);
    executionOnLocal();
    InputDeviceInfo();
    window.addEventListener('resize', InputDeviceInfo);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', InputDeviceInfo);
  });

</script>

<template>
    <OrganismSplashCluvi v-if="flatSplashScreenCluvi || FlagsAreLoading"></OrganismSplashCluvi>
    <div v-else :class="`modeMenu-${flagCssCustomObj.modeMenu}`">
      <Header />
      <div class="wrapShift wrapShift-fixed" :key="renderKey">
        <RouterView  />
      </div>
      <FooterLink v-if="!isMenuPdfView" />
      <ModalLang
        v-if="langModal && !isMenuPdfView"
        :customerData="customerData" :defaultLanguageLocalStorage="defaultLanguageLocalStorage"
        @setI18nFileByLang="setI18nFileByLang"
        @closeModalLang="closeModalLang"
      />
      <MenuOpciones
        v-if="menuOptionsModal && !isMenuPdfView"
        :menuData="menuData" :defaultLanguageLocalStorage="defaultLanguageLocalStorage" :offRate="offRate"
        @setI18nFileByLang="setI18nFileByLang"
        @saveLanguageModalButton="saveLanguageModalButton"
      />
      <buttonRatings v-if="floatButtonRate && (trakingData.supplier_id !== 22569 && trakingData.supplier_id !== 17545 ) && !isMenuPdfView" @openModalRate="openModalRate" @hiddenButtonFloat="hiddenButtonFloat" />
      <buttonLeftRatings v-if="floatButtonRate && (trakingData.supplier_id === 22569 || trakingData.supplier_id === 17545 ) && !isMenuPdfView" @openModalRate="openModalRate" @hiddenButtonFloat="hiddenButtonFloat" />
      <Modal v-if="(menuData.upselling_products?.length ||  menuData.content_popups?.length) && !isMenuPdfView" />
      <Transition enter-active-class="slideInRight" leave-active-class="slideOutRight">
        <ProductDetail v-if="displayProduct"/>
      </Transition>
      <ModalRatingsStar
        v-if="openModalRatingStar && !isMenuPdfView"
        :customerData="customerData" :profileData="profileData" :trakingData="trakingData"
        @closeModalRatingsStar="closeModalRatingsStar" @hiddenButtonFloat="hiddenButtonFloat"
      />
      <ButtonViewMenu
        v-if="
            (flagCssCustomObj.modeMenu === 'banners' || flagCssCustomObj.flagViewButtonRightCategories) &&
            route.name !== 'BannerCategories' &&
            !isMenuPdfView
          "
        :customerData="customerData"
      />
      <CustomButtonFloat
        v-if="showCustomButtonFloat && !isMenuPdfView "
        label="Service Room"
        icon="fa-brands fa-whatsapp"
        position="right"
        url="https://api.whatsapp.com/send/?phone=573202466021&text=Bienvenido+a+Ocaso+%2C+que+planes+tienes+para+hoy%3F&type=phone_number&app_absent=0"
        :showCloseButton="false"
        :targetBlank="true"
        @click="handleCustomButtonClick"
        @close="handleCustomButtonClose"
      />
    </div>
</template>